import React, { FC, useEffect } from "react";

import { startOfDay } from "date-fns/esm";

import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { DatePicker } from "@mapmycustomers/ui";

import { formatDate } from "@app/util/formatters";
import useStateWithTransformer from "@app/util/hook/useStateWithTransformer";
import isValidDate from "@app/util/isValidDate";
import { parseApiDateWithTz } from "@app/util/parsers";

import EditorComponentProps from "./EditorComponentProps";

type MaybeDate = Date | undefined;

const useDateState = (initialState: (() => MaybeDate) | MaybeDate) =>
  useStateWithTransformer<MaybeDate>(initialState, (date: MaybeDate) =>
    date ? startOfDay(date) : date
  );

const formatForApi = (date: Date) => formatDate(date, "yyyy-MM-dd");

export interface AbsoluteDatesEditorProps extends EditorComponentProps<string> {}

const AbsoluteDatesEditor: FC<AbsoluteDatesEditorProps> = ({
  duplicate,
  onChange,
  value,
  ...props
}) => {
  const [internalValue, setInternalValue] = useDateState(undefined);
  useEffect(() => {
    if (!value) {
      setInternalValue(undefined);
    } else {
      const parsedDate = parseApiDateWithTz(value);
      setInternalValue(isValidDate(parsedDate) ? parsedDate : undefined);
    }
  }, [setInternalValue, value]);

  const duplicateError =
    internalValue !== undefined && formatForApi(internalValue) !== value && !!duplicate;

  // wrapping onChange in dynamic callback to prevent triggering useEffect
  // because of the onChange changes
  const handleChange = useDynamicCallback((value: string) => onChange?.(value));
  useEffect(() => {
    if (internalValue === undefined) {
      return;
    }
    handleChange(formatForApi(internalValue));
  }, [duplicateError, internalValue, handleChange]);

  return (
    <DatePicker
      error={duplicateError ? "Break point already exists" : ""}
      format="PP"
      onChange={setInternalValue}
      value={internalValue}
      {...props}
    />
  );
};

export default AbsoluteDatesEditor;
